import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Grid, Stack, Typography, Button, TextField, Autocomplete,
  MenuItem, Checkbox, FormControlLabel, Alert, Divider, Paper,
  Avatar, LinearProgress, alpha,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AutoAwesomeMotionIcon from '@mui/icons-material/AutoAwesomeMotion';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import GavelIcon from '@mui/icons-material/Gavel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Link } from 'react-router-dom';
import apiService, { authFetch } from '../../services/apiServices';
import warningsLocal from '../../services/warningsLocalService';
import helper from '../../services/helper';
import normalizeWorker, { contactEmailOf } from '../../services/workerNormalizer';
import WorkerPicker from '../common/WorkerPicker';
import { getWarningPdfBase64, getWarningPdfFilename } from '../../services/warningPdfService';

const SEVERITY_LEVELS = [
  { value: 'Kujtesë',         label: 'Kujtesë',          color: '#475569', bg: '#F1F5F9', desc: 'Kujtesë verbale ose e lehtë' },
  { value: 'Vërejtje',        label: 'Vërejtje',         color: '#B45309', bg: '#FFFBEB', desc: 'Vërejtje zyrtare me shkrim' },
  { value: 'Shkelje e rëndë', label: 'Shkelje e rëndë',  color: '#B91C1C', bg: '#FEF2F2', desc: 'Shkelje e rëndë disiplinore' },
];

function initials(first = '', last = '') {
  return `${first[0] || ''}${last[0] || ''}`.toUpperCase() || '?';
}

// Etiketë seksioni — e njëjta gjuhë si te "Rregullore & Procedura":
// caption 0.72rem, uppercase, slate. `subtitle2` ishte dy hapa më i madh dhe
// e bënte formularin të dukej si faqe me tituj, jo si formular.
function SectionLabel({ icon, children }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" mb={1.25}>
      {icon}
      <Typography variant="caption" fontWeight={700} color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.72rem' }}>
        {children}
      </Typography>
    </Stack>
  );
}

const FIELD_SX  = { '& .MuiOutlinedInput-root': { borderRadius: 1.5 } };
const PANEL_SX  = { borderRadius: 2.5, p: { xs: 1.75, sm: 2.25 } };
const BTN_SX    = { borderRadius: 2, textTransform: 'none', fontWeight: 700, boxShadow: 'none' };

export default function AddWarning() {
  const [employees, setEmployees] = useState([]);
  const [employee, setEmployee] = useState('');
  const [subject, setSubject] = useState('Vërejtje');
  const [severity, setSeverity] = useState('Kujtesë');
  const [description, setDescription] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState('success');
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const syncingRef = useRef(false);

  useEffect(() => {
    apiService().get('workers').then((res) => {
      if (res?.success === false) return;
      const raw = res?.data || res;
      if (Array.isArray(raw)) {
        setEmployees(raw.map((w, idx) => normalizeWorker(w, idx)));
      }
    });
    try {
      const id = helper().getParameterByName('employee');
      if (id) setEmployee(String(id));
    } catch (_) {}
    // Vetëm shabllonet e ndezura: te Konfigurimi mund të rrijnë edhe të fikura,
    // por këtu ato do të ishin zgjedhje që dikush i ka hequr me qëllim.
    authFetch('/api/warning-templates?active=1')
      .then(r => r.json())
      .then(r => { if (r?.success) setTemplates(r.data || []); })
      .catch(() => {});   // pa shabllone, formulari mbetet plotësisht i përdorshëm
  }, []);

  /**
   * Vendmbajtësit që i di sistemi plotësohen vetë.
   *
   * «[Emri Mbiemri]» dhe «[Data]» nuk janë vendime të HR-së: emri vjen nga
   * punëtori i zgjedhur, data është sot. Të lëna me dorë, ato dilnin ashtu siç
   * ishin — te baza ka vërejtje të ruajtura që nisin me «Punëmarrësi [Emri
   * Mbiemri] ka munguar nga puna më [Data]». Ai është dokument zyrtar, jo draft.
   *
   * Kllapat e tjera — «[Përshkrimi i rastit]» e të ngjashme — mbeten, sepse ato
   * vërtet i shkruan njeriu.
   */
  const fillKnownPlaceholders = useCallback((text, employeeId) => {
    if (!text) return text;
    const emp = employees.find(e => String(e.Id) === String(employeeId));
    const fullName = [emp?.FirstName, emp?.LastName].filter(Boolean).join(' ').trim();
    const today = new Date().toLocaleDateString('de-DE');
    let out = text;
    if (fullName) {
      out = out.replace(/\[Emri Mbiemri\]/g, fullName).replace(/\[Emri dhe Mbiemri\]/g, fullName);
    }
    return out.replace(/\[Data\]/g, today).replace(/\[Data e Njoftimit\]/g, today);
  }, [employees]);

  // Shablloni MBUSH fushat, nuk i mbyll: teksti i tij është pikënisje.
  const applyTemplate = useCallback((id) => {
    setTemplateId(id);
    const t = templates.find(x => String(x.id) === String(id));
    if (!t) return;
    if (t.severity) setSeverity(t.severity);
    if (t.subject)  setSubject(t.subject);
    if (t.comment)  setDescription(fillKnownPlaceholders(t.comment, employee));
  }, [templates, employee, fillKnownPlaceholders]);

  /**
   * Radha e mbushjes nuk duhet të vendosë përmbajtjen.
   *
   * Kur shablloni zgjidhet PARA punëtorit — radha e natyrshme kur HR-ja nis nga
   * lloji i shkeljes — emri ende s'dihet, dhe «[Emri Mbiemri]» mbetej në tekst.
   * Zëvendësimi përsëritet sa herë ndryshon punëtori; prek vetëm kllapat e
   * njohura, ndaj s'e cenon asnjë fjalë të shkruar me dorë.
   */
  useEffect(() => {
    if (!employee) return;
    setDescription(prev => fillKnownPlaceholders(prev, employee));
    setSubject(prev => fillKnownPlaceholders(prev, employee));
  }, [employee, fillKnownPlaceholders]);

  const saveWarningToServer = useCallback(async (rawPayload) => {
    const { Id, id, sendEmail: _se, EmployeeEmail, EmployeeName, syncedToDb, _local, ...rest } = rawPayload || {};
    const payload = {
      ...rest,
      Employee: rest.Employee ?? rest.EmployeeId ?? rest.employeeId ?? rest.employee,
      CreatedAt: rest.CreatedAt || new Date().toISOString(),
    };
    if (payload.Employee !== undefined) {
      payload.Employee = Number(payload.Employee) || payload.Employee;
    }
    const tryTypes = ['employeeWarnings', 'employeeWarning', 'warnings'];
    let res = null;
    for (const t of tryTypes) {
      const objs = [{ id: 0, entity: payload, status: 1, type: t }];
      try {
        res = await apiService().saveChanges(objs);
      } catch (e) {
        res = { success: false, errorMessage: e?.message };
      }
      if (res?.success) break;
    }
    if (!res?.success) return null;
    // ID-ja e rreshtit të ri kthehet nga serveri — dhe deri tani hidhej poshtë.
    // Pa të, kopja lokale ruhej me një ID të sajuar (`sent-<koha>`), dhe raporti
    // s'kishte si ta njihte si të njëjtën vërejtje: e njëjta gjë dilte dy herë,
    // një herë nga baza e një herë nga localStorage. Një krijim, dy rreshta.
    const rec = Array.isArray(res.data) ? res.data.find(x => x?.success)?.data : res.data;
    return { Id: rec?.Id ?? rec?.id ?? null };
  }, []);

  const sendEmailNotification = useCallback(
    async (warningPayload) => {
      const wantsEmail = warningPayload?.sendEmail !== false;
      if (!wantsEmail) return { ok: true };
      const worker = employees.find((e) => String(e.Id) === String(warningPayload.Employee));
      const email = contactEmailOf(worker) || warningPayload?.EmployeeEmail;
      // Pa adresë kthehej `ok: true` — dhe formulari thoshte «email-i u dërgua»
      // ndërsa s'ishte dërguar asgjë. 24 nga 28 punëtorët këtu s'kanë email,
      // ndaj kjo ishte rruga e zakonshme, jo përjashtimi.
      if (!email) return { ok: false, reason: 'Punëtori nuk ka email të regjistruar — njoftimi nuk u dërgua.' };
      const firstName  = worker?.FirstName  || warningPayload?.EmployeeName?.split(' ')[0] || '';
      const lastName   = worker?.LastName   || warningPayload?.EmployeeName?.split(' ').slice(1).join(' ') || '';
      const workerName = warningPayload?.EmployeeName || [worker?.FirstName, worker?.LastName].filter(Boolean).join(' ') || '';

      let pdfBase64   = null;
      let pdfFilename = null;
      try {
        const enriched = {
          ...warningPayload,
          FirstName:      firstName,
          LastName:       lastName,
          Email:          email,
          Position:       worker?.Position       || '',
          DepartmentName: worker?.DepartmentName || '',
        };
        pdfBase64   = getWarningPdfBase64(enriched);
        pdfFilename = getWarningPdfFilename(enriched);
        if (!pdfBase64) console.warn('[PDF] getWarningPdfBase64 ktheu null/empty');
      } catch (pdfErr) {
        console.error('[PDF] Gabim gjatë gjenerimit të PDF-it të vërejtjes:', pdfErr);
      }

      const emailPayload = {
        to: email,
        subject: `Vërejtje: ${warningPayload?.Subject || 'Vërejtje'}`,
        body: `Përshëndetje ${workerName || ''},\n\nJu është dhënë një vërejtje (${warningPayload?.Severity || ''}).\n\nSubjekti: ${warningPayload?.Subject || '—'}\n\n${warningPayload?.Description || ''}\n\nFaleminderit,\nDepartamenti HR`,
        pdfBase64,
        pdfFilename,
      };
      try {
        const res = await apiService().execute('sendWarningEmail', emailPayload, 'post');
        if (res?.success) return { ok: true };
        return { ok: false, reason: res?.error || 'Email-i nuk u dërgua.' };
      } catch (e) {
        return { ok: false, reason: e?.message || 'Gabim gjatë dërgimit të email-it.' };
      }
    },
    [employees]
  );

  const syncLocalWarnings = useCallback(async () => {
    if (syncingRef.current) return;
    const pending = warningsLocal().list().filter((it) => !it._sent);
    if (!pending.length) return;
    syncingRef.current = true;
    let synced = 0;
    let emailPending = 0;
    try {
      for (const item of pending) {
        const alreadyInDb = item.syncedToDb === true;
        const saved = alreadyInDb ? true : await saveWarningToServer(item);
        if (!saved) continue;
        const emailResult = await sendEmailNotification(item);
        if (emailResult?.ok) {
          warningsLocal().remove(item.Id);
          synced += 1;
        } else {
          warningsLocal().update(item.Id, { syncedToDb: true });
          emailPending += 1;
        }
      }
    } finally {
      syncingRef.current = false;
    }
    if (synced || emailPending) {
      const parts = [];
      if (synced) parts.push(`${synced} vërejtje u dërguan në DB`);
      if (emailPending) parts.push(`${emailPending} email-e në pritje`);
      setMsg(parts.join('. ') + '.');
      setMsgType('info');
    }
  }, [saveWarningToServer, sendEmailNotification]);

  useEffect(() => {
    const onOnline = () => syncLocalWarnings();
    window.addEventListener('online', onOnline);
    syncLocalWarnings();
    return () => window.removeEventListener('online', onOnline);
  }, [syncLocalWarnings]);

  const onSave = async () => {
    setMsg('');
    if (!employee || !subject) {
      setMsg('Plotëso punëtorin dhe subjektin.');
      setMsgType('error');
      return;
    }
    // Vendmbajtësit e mbetur ndalen këtu. Vërejtja shkon me email dhe bëhet PDF
    // zyrtar; «[Përshkrimi i rastit]» brenda saj nuk është gabim shtypi që
    // rregullohet më vonë — dokumenti ka dalë tashmë.
    const leftovers = [...new Set(`${subject} ${description}`.match(/\[[^\]\n]{2,40}\]/g) || [])];
    if (leftovers.length) {
      setMsg(`Teksti ka ende vende për t'u plotësuar: ${leftovers.join(', ')}. Zëvendësoji para se ta ruash.`);
      setMsgType('error');
      return;
    }
    setSaving(true);
    const emp = employees.find((e) => String(e.Id) === String(employee));
    const payload = {
      Employee: Number(employee) || employee,
      Subject: subject,
      Severity: severity,
      Description: description,
      CreatedAt: new Date().toISOString(),
    };
    const offlineCopy = {
      ...payload,
      sendEmail,
      EmployeeEmail: contactEmailOf(emp),
      EmployeeName: [emp?.FirstName, emp?.LastName].filter(Boolean).join(' ').trim(),
      syncedToDb: false,
    };
    try {
      const saved = await saveWarningToServer(payload);
      if (saved) {
        const savedId = saved.Id ?? null;
        let emailOk = true;
        let emailErrReason = '';
        if (sendEmail) {
          const emailResult = await sendEmailNotification({ ...offlineCopy, sendEmail });
          emailOk = emailResult?.ok !== false;
          emailErrReason = emailResult?.reason || '';
          if (!emailOk) {
            warningsLocal().add({ ...offlineCopy, Id: savedId, syncedToDb: true });
          }
        }
        warningsLocal().addSent({ ...offlineCopy, Id: savedId, syncedToDb: true, emailSent: emailOk });
        // Kur njoftimi s'është kërkuar fare, «email-i u dërgua» është thjesht
        // e pavërtetë — dhe ishte teksti që dilte edhe atëherë.
        setMsg(!sendEmail
          ? 'Vërejtja u ruajt. Njoftimi me email nuk u kërkua.'
          : emailOk
            ? `Vërejtja u ruajt dhe njoftimi u dërgua te ${offlineCopy.EmployeeEmail}.`
            : `Vërejtja u ruajt. ${emailErrReason || 'Email-i nuk u dërgua — kontrolloni konfigurimin SMTP në Settings.'}`
        );
        setMsgType(!sendEmail ? 'success' : emailOk ? 'success' : 'warning');
        setDescription('');
        // Edhe zgjedhja e shabllonit pastrohet: përndryshe kutia do të tregonte
        // ende shabllonin e vërejtjes që sapo u dërgua, mbi një përshkrim bosh.
        setTemplateId('');
      } else {
        const localItem = warningsLocal().add(offlineCopy);
        warningsLocal().addSent({ ...offlineCopy, Id: localItem?.Id });
        setMsg('Vërejtja u ruajt lokalisht. Do të sinkronizohet automatikisht.');
        setMsgType('warning');
        syncLocalWarnings();
      }
    } catch (e) {
      const localItem = warningsLocal().add(offlineCopy);
      warningsLocal().addSent({ ...offlineCopy, Id: localItem?.Id });
      setMsg('Vërejtja u ruajt lokalisht. Do të sinkronizohet automatikisht.');
      setMsgType('warning');
      syncLocalWarnings();
    } finally {
      setSaving(false);
    }
  };

  const selectedEmp = employees.find(e => String(e.Id) === String(employee));
  /**
   * A do të dalë vërtet një email — dhe te cila adresë.
   *
   * Kutia «Dërgo njoftim» rrinte e ndezur edhe kur punëtori s'kishte adresë, dhe
   * pas ruajtjes formulari njoftonte «email-i u dërgua». Këtu, në 24 nga 28
   * punëtorët, kjo ishte gjendja e zakonshme. Tani zgjedhja tregohet para se të
   * shtypet butoni, dhe butoni e thotë vetë çfarë do të bëjë.
   */
  const recipient = contactEmailOf(selectedEmp);
  const willSend = Boolean(sendEmail && recipient);
  const selectedSev = SEVERITY_LEVELS.find(s => s.value === severity);
  const sevColor = selectedSev?.color || '#475569';

  return (
    <Box>
      {/* Koka */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={2}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar sx={{
            width: 36, height: 36, borderRadius: 1.5,
            bgcolor: alpha(sevColor, 0.1), color: sevColor,
            transition: 'background-color .25s, color .25s',
          }}>
            <WarningAmberIcon sx={{ fontSize: 20 }} />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={800} lineHeight={1.2} color="text.primary">Shto Vërejtje</Typography>
            <Typography variant="caption" color="text.secondary">Lësho një vërejtje zyrtare për punëtorin</Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button component={Link} to="/warnings" variant="outlined" size="small"
            startIcon={<ArrowBackIcon />} sx={BTN_SX}>
            Kthehu
          </Button>
          <Button variant="contained" size="small" startIcon={willSend ? <SendIcon /> : <SaveIcon />}
            onClick={onSave} disabled={saving} sx={BTN_SX}>
            {saving
              ? (willSend ? 'Duke ruajtur dhe dërguar…' : 'Duke ruajtur…')
              : (willSend ? 'Ruaj dhe dërgo' : 'Ruaj vërejtjen')}
          </Button>
        </Stack>
      </Stack>

      {saving && <LinearProgress sx={{ mb: 1.5, borderRadius: 1 }} />}

      {msg && (
        <Alert severity={msgType} sx={{ mb: 1.5, borderRadius: 2 }} onClose={() => setMsg('')}>
          {msg}
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Formulari */}
        <Grid item xs={12} md={7} lg={8}>
          <Paper variant="outlined" sx={PANEL_SX}>

            <SectionLabel icon={<PersonOutlineIcon sx={{ fontSize: 16, color: 'text.disabled' }} />}>
              Punëtori
            </SectionLabel>

            <WorkerPicker
              workers={employees}
              value={employee}
              onChange={(v) => setEmployee(v ?? '')}
              label="Zgjedh punëtorin"
              placeholder="Shkruaj emrin, mbiemrin ose email-in…"
              sx={FIELD_SX}
            />

            <Divider sx={{ my: 2 }} />

            <SectionLabel icon={<GavelIcon sx={{ fontSize: 16, color: 'text.disabled' }} />}>
              Detajet e vërejtjes
            </SectionLabel>

            {/* Shablloni — shfaqet vetëm nëse ka. Një zgjedhës bosh do të
                premtonte diçka që Konfigurimi nuk e ka ende. */}
            {templates.length > 0 && (
              <Autocomplete
                size="small" fullWidth
                options={templates}
                value={templates.find(t => String(t.id) === String(templateId)) || null}
                onChange={(_, t) => applyTemplate(t ? t.id : '')}
                getOptionLabel={(t) => t?.label || ''}
                isOptionEqualToValue={(o, v) => String(o.id) === String(v?.id)}
                openOnFocus autoHighlight blurOnSelect
                noOptionsText="Asnjë shabllon nuk përputhet"
                ListboxProps={{ style: { maxHeight: 260 } }}
                renderOption={(props, t) => {
                  const sev = SEVERITY_LEVELS.find(x => x.value === t.severity);
                  return (
                    <Box component="li" {...props} key={t.id}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 13.5, py: 0.75 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                        bgcolor: sev?.color || '#94A3B8' }} />
                      <span>{t.label}</span>
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Shablloni (opsional)"
                    placeholder="Kërko shabllon…"
                    helperText="Mbush nivelin, subjektin dhe përshkrimin — të gjitha mbeten të ndryshueshme."
                    sx={{ ...FIELD_SX, mb: 1.5 }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <AutoAwesomeMotionIcon sx={{ fontSize: 16, color: 'text.disabled', ml: 0.5, mr: 0.5 }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                select size="small" label="Niveli i vërejtjes"
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
                sx={{ ...FIELD_SX, width: { xs: '100%', sm: 200 } }}
                InputLabelProps={{ shrink: true }}
              >
                {SEVERITY_LEVELS.map(s => (
                  <MenuItem key={s.value} value={s.value}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: s.color, flexShrink: 0 }} />
                      <span>{s.label}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small" label="Subjekti" value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="p.sh. Vonesa të përsëritura në punë"
                sx={{ ...FIELD_SX, flex: 1 }}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>

            {selectedSev && (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.25, pl: 0.25 }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: selectedSev.color, flexShrink: 0 }} />
                <Typography variant="caption" color="text.disabled">
                  <Box component="span" sx={{ color: selectedSev.color, fontWeight: 700 }}>{selectedSev.label}</Box>
                  {' — '}{selectedSev.desc}
                </Typography>
              </Stack>
            )}

            <Divider sx={{ my: 2 }} />

            <SectionLabel icon={<DescriptionOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />}>
              Përshkrimi
            </SectionLabel>

            <TextField
              fullWidth multiline minRows={6} size="small"
              label="Përshkrimi i detajuar"
              placeholder="Shkruaj detajet e vërejtjes, arsyen, kontekstin…"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.875rem', lineHeight: 1.7 } }}
              InputLabelProps={{ shrink: true }}
            />

            <Divider sx={{ my: 2 }} />

            <SectionLabel icon={<EmailOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />}>
              Njoftim
            </SectionLabel>

            <FormControlLabel
              sx={{ ml: 0 }}
              control={
                <Checkbox size="small" checked={sendEmail && Boolean(recipient)}
                  disabled={Boolean(selectedEmp) && !recipient}
                  onChange={(e) => setSendEmail(e.target.checked)} color="success" />
              }
              label={
                <Typography variant="body2" fontWeight={500}>
                  Dërgo njoftim me email te punëtori
                  {willSend && (
                    <Box component="span" sx={{ color: 'text.disabled', fontWeight: 400 }}>
                      {' · '}{recipient}
                    </Box>
                  )}
                </Typography>
              }
            />
            {selectedEmp && !recipient && (
              <Alert severity="info" sx={{ mt: 1, py: 0.25, fontSize: '.8rem' }}>
                {`${[selectedEmp.FirstName, selectedEmp.LastName].filter(Boolean).join(' ')} nuk ka email të regjistruar.`}
                {' '}Vërejtja ruhet dhe mbetet te lista; njoftimi nuk mund të dërgohet derisa adresa të shtohet te dosja e punëtorit.
              </Alert>
            )}
          </Paper>
        </Grid>

        {/* Anësorja */}
        <Grid item xs={12} md={5} lg={4}>
          <Stack spacing={1.5} sx={{ position: { md: 'sticky' }, top: { md: 8 } }}>

            {selectedEmp && (
              <Paper variant="outlined" sx={PANEL_SX}>
                <SectionLabel icon={<PersonOutlineIcon sx={{ fontSize: 16, color: 'text.disabled' }} />}>
                  Punëtori i zgjedhur
                </SectionLabel>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main', fontWeight: 700, fontSize: 14 }}>
                    {initials(selectedEmp.FirstName, selectedEmp.LastName)}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                      {selectedEmp.FirstName} {selectedEmp.LastName}
                    </Typography>
                    {selectedEmp.Position && (
                      <Typography variant="caption" color="text.secondary" display="block">{selectedEmp.Position}</Typography>
                    )}
                    {selectedEmp.Email && (
                      <Typography variant="caption" color="text.disabled" display="block" sx={{ wordBreak: 'break-word' }}>
                        {selectedEmp.Email}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Paper>
            )}

            {/* Nivelet — jo vetëm legjendë: klikimi e zgjedh nivelin */}
            <Paper variant="outlined" sx={PANEL_SX}>
              <SectionLabel icon={<InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />}>
                Nivelet e vërejtjeve
              </SectionLabel>
              <Stack spacing={0.75}>
                {SEVERITY_LEVELS.map(s => {
                  const on = severity === s.value;
                  return (
                    <Box
                      key={s.value}
                      onClick={() => setSeverity(s.value)}
                      sx={{
                        px: 1.25, py: 1, borderRadius: 2, cursor: 'pointer',
                        bgcolor: on ? s.bg : 'transparent',
                        border: '1px solid',
                        borderColor: on ? alpha(s.color, 0.35) : 'divider',
                        transition: 'all .15s',
                        '&:hover': { borderColor: alpha(s.color, 0.35), bgcolor: s.bg },
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.color, flexShrink: 0 }} />
                        <Typography variant="body2" fontWeight={700} sx={{ color: on ? s.color : 'text.primary' }}>
                          {s.label}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', pl: 2.25 }}>
                        {s.desc}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ ...PANEL_SX, bgcolor: '#F8FAFC', borderColor: 'divider' }}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <InfoOutlinedIcon sx={{ color: 'text.disabled', fontSize: 16, mt: 0.15 }} />
                <Box>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">
                    Ruajtje automatike offline
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    Pa lidhje me serverin, vërejtja ruhet lokalisht dhe sinkronizohet vetë kur ktheheni online.
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
